import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import {
  AddOnItem,
  AddOnItemName,
  SubscriptionPortalFlow,
  SubscriptionType,
} from "schemas/dashboard";

import { SubscriptionMessages } from "features/Home/UpgradeDialog/Plan/helpers";
import { fetcherAuth } from "utils/api";
import { formatEpochTimestamp } from "utils/time";
import { useSubscription } from "utils/useSubscription";

import styles from "./styles";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ManageCustomEmailDialog({ open, onClose }: Props) {
  const { getToken } = useAuth();
  const { subscription, setSubscription } = useContext(SubscriptionContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { handleManagePlan, checkoutLoading } = useSubscription();
  const [loading, setLoading] = useState<AddOnItem | null>(null);

  const handleCancel = async (emailVolume: AddOnItem) => {
    setLoading(emailVolume);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${emailVolume?.id}/manage`,
        "POST",
        {},
        {
          flow: SubscriptionPortalFlow.subscription_cancel,
        },
      );
      window.open(res.session.url, "_self");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(null);
    }
  };

  const renewSubscription = async (emailVolume: AddOnItem) => {
    try {
      const { addOnItem } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/${emailVolume?.id}/renew`,
        "POST",
        {},
      );
      if (addOnItem) {
        setSubscription((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            emailVolumes: prev.emailVolumes?.map((ev) =>
              ev.id === addOnItem.id ? addOnItem : ev,
            ),
          };
        });
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <IconButton
        sx={{
          color: "primary.main",
          fontSize: 14,
          position: "absolute",
          right: 10,
          top: 10,
        }}
        onClick={onClose}
      >
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <DialogTitle>Manage Email Volume Packages</DialogTitle>

      <DialogContent>
        <Typography paragraph>
          You currently have{" "}
          <strong>{subscription?.emailVolumes?.length || 0} package(s)</strong>{" "}
          of email volumes purchased. Use the link below to modify your payment
          method, see the next billing date for all your packages and your
          invoice history.
        </Typography>

        <LoadingButton
          variant="outlined"
          onClick={() => handleManagePlan(AddOnItemName.email_volume_monthly)}
          loading={checkoutLoading === AddOnItemName.email_volume_monthly}
        >
          Manage Packages
        </LoadingButton>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" paragraph>
          Cancel Email Volume Packages
        </Typography>
        {subscription?.emailVolumes?.map(
          (emailVolume, index) =>
            (emailVolume?.status === SubscriptionType.ACTIVE ||
              emailVolume?.status === SubscriptionType.PAST_DUE) && (
              <Grid
                sx={{
                  backgroundColor: "#f9f9f9",
                  p: 2,
                  mb: 1,
                  borderRadius: 2,
                }}
              >
                <Grid item>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    Package {index + 1}{" "}
                    {emailVolume?.itemName ===
                    AddOnItemName.email_volume_monthly ? (
                      <Chip
                        sx={styles.annotation}
                        label="Monthly"
                        size="small"
                      />
                    ) : (
                      <Chip
                        sx={styles.annotation}
                        label="Yearly"
                        size="small"
                      />
                    )}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 10, color: grey[600], my: 1 }}
                  ></Typography>

                  {emailVolume?.cancelAt && (
                    <Typography>
                      This package will be cancelled on{" "}
                      <strong>
                        {emailVolume &&
                          formatEpochTimestamp({
                            epochTimestamp: emailVolume.cancelAt,
                            addYear: true,
                          })}
                      </strong>
                      . To continue using this package after this date, please
                      resubscribe using the button below.
                    </Typography>
                  )}
                  {emailVolume?.status && !emailVolume?.cancelAt && (
                    <Typography>
                      You purchased this package on{" "}
                      {emailVolume &&
                        formatEpochTimestamp({
                          epochTimestamp: emailVolume.createdAt,
                          addYear: true,
                        })}
                      .{" "}
                      {emailVolume?.status === SubscriptionType.ACTIVE ? (
                        <>
                          Your subscription is <strong>active</strong>{" "}
                          <Box
                            sx={{ color: "green.main" }}
                            component="i"
                            className="fa-solid fa-check-circle"
                          />
                        </>
                      ) : (
                        SubscriptionMessages[emailVolume.status]
                      )}
                    </Typography>
                  )}

                  {!emailVolume?.cancelAt &&
                    emailVolume?.status === SubscriptionType.ACTIVE && (
                      <Box sx={{ mt: 2 }}>
                        <LoadingButton
                          sx={{ ml: -1 }}
                          color="secondary"
                          onClick={() => handleCancel(emailVolume)}
                          loading={loading?.id === emailVolume?.id}
                        >
                          Cancel
                        </LoadingButton>
                      </Box>
                    )}

                  {emailVolume?.cancelAt &&
                    emailVolume?.status === SubscriptionType.ACTIVE && (
                      <Button
                        sx={{ ml: -1 }}
                        onClick={() => renewSubscription(emailVolume)}
                      >
                        Resubscribe
                      </Button>
                    )}
                </Grid>
              </Grid>
            ),
        )}
      </DialogContent>
    </Dialog>
  );
}
